// WordCloud api
import { defaultAxios } from '@/services';
import { createFeatureApiConfig } from '@/services/apiMockConfig';
import type { FeedbackListItemWithBlock } from '@/types/feedback';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import type { WordCloudBasis } from '../types';
import { mockWordCloudService } from './mock';

export const sanitizeKeywordValue = (keyword: string): string => {
  return keyword
    .trim()
    .replace(/^[@#]+/, '')
    .trim();
};

export const resolveFeedbackKeywordFilterKey = (
  basis?: WordCloudBasis,
  filter?: StructuredFilter,
): FEEDBACK_COLUMNS => {
  if (basis === 'hashtags') {
    return FEEDBACK_COLUMNS.HASHTAG;
  }
  if (basis === 'mentions') {
    return FEEDBACK_COLUMNS.MENTION;
  }
  if (Array.isArray(filter?.[FEEDBACK_COLUMNS.HASHTAG])) {
    return FEEDBACK_COLUMNS.HASHTAG;
  }
  if (Array.isArray(filter?.[FEEDBACK_COLUMNS.MENTION])) {
    return FEEDBACK_COLUMNS.MENTION;
  }
  return FEEDBACK_COLUMNS.KEYWORD;
};

const createWordCloudFeedbackFilter = (payload: {
  filter?: StructuredFilter;
  keyword: string;
  basis?: WordCloudBasis;
}): StructuredFilter => {
  const filter = { ...(payload.filter || {}) };
  const filterKey = resolveFeedbackKeywordFilterKey(
    payload.basis,
    payload.filter,
  );
  const keyword = sanitizeKeywordValue(payload.keyword);
  if (!keyword) return filter;
  const currentValueSets = Array.isArray(filter[filterKey])
    ? (filter[filterKey] as StructuredFilterStringNumberValueSet[])
    : [];

  filter[filterKey] = [
    ...currentValueSets,
    {
      op: STRUCTURED_FILTER_OPERATOR.OR,
      values: [keyword],
    },
  ];

  return filter;
};

export const realWordCloudService = {
  fetchWordCloudFeedback: async (
    workspace_alias: string,
    sortStatus: NotNullableSortStatusT,
    payload: {
      filter?: StructuredFilter;
      keyword: string;
      paginate_token?: string;
      basis?: WordCloudBasis;
    },
    isPublic?: boolean,
  ): Promise<{
    count: number;
    feedbacks: PaginatedData<FeedbackListItemWithBlock>[];
  }> => {
    const url = isPublic
      ? `/workspaces/${workspace_alias}/feedbacks/with_block/public`
      : `/workspaces/${workspace_alias}/feedbacks/with_block`;

    const requestFilter = createWordCloudFeedbackFilter(payload);
    const response = await defaultAxios.post<FeedbackBlockListT>(
      url,
      requestFilter,
      {
        params: {
          sortby: sortStatus.sortBy,
          order: sortStatus.orderStatus,
          cursor: payload.paginate_token,
          direction: 'next',
        },
      },
    );
    return {
      count: response.data.count,
      feedbacks: response.data.feedbacks || [],
    };
  },
};

export const wordCloudApiConfig = createFeatureApiConfig('wordCloud', {
  defaultUseMock: false,
  enableConsoleHelpers: true,
});

export const wordCloudService = wordCloudApiConfig.isMockMode()
  ? {
      ...realWordCloudService,
      ...mockWordCloudService,
    }
  : realWordCloudService;

// Translation exports
// Translation exports
export type {
  TranslateWordCloudParams,
  TranslateWordCloudResult,
} from './translateWordCloud';
export {
  isTranslatorSupported,
  translateWordCloud,
} from './translateWordCloud';
export { useTranslateWordCloudMutation } from './useTranslateWordCloudMutation';
